// Request/response contracts shared by server routes and client calls.
import { z } from "zod";
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const passwordSchema = z.string().min(10, "Use at least 10 characters").max(200);
export const registerBody = z.object({
    email: emailSchema,
    password: passwordSchema,
    name: z.string().trim().min(1).max(80),
});
export const loginBody = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
});
export const userRoles = ["member", "admin"];
/** Reader preferences. Stored per user, mirrored in localStorage for visitors. */
export const readerSettings = z.object({
    font: z.enum(["serif", "sans"]).default("serif"),
    size: z.number().int().min(-2).max(3).default(0),
    theme: z.enum(["system", "light", "dark"]).default("system"),
    verseNumbers: z.boolean().default(true),
    headings: z.boolean().default(true),
    footnotes: z.boolean().default(true),
    crossrefs: z.boolean().default(true),
    redLetter: z.boolean().default(false),
    highlights: z.boolean().default(true),
    /** A public-domain translation shown in a second column, or "none". */
    parallel: z.enum(["none", "WEB", "BSB", "KJV"]).default("none"),
});
export const DEFAULT_READER_SETTINGS = readerSettings.parse({});
export const ESV_COPYRIGHT = "Scripture quotations are from the ESV® Bible (The Holy Bible, English Standard Version®), © 2001 by Crossway, a publishing ministry of Good News Publishers. Used by permission. All rights reserved.";
/* ---------------- Commentary ---------------- */
export const commentaryStatuses = ["draft", "published"];
/** Longest chapter in the canon is Psalm 119 at 176 verses. */
const verseNumber = z.number().int().min(1).max(200);
const verseRange = {
    /** The verses this block is about; both null means "no particular verse". */
    startVerse: verseNumber.nullable().default(null),
    endVerse: verseNumber.nullable().default(null),
};
const rangeIsOrdered = (v) => v.startVerse === null || v.endVerse === null || v.endVerse >= v.startVerse;
const rangeError = () => ({ message: "A verse range ends before it starts", path: ["endVerse"] });
/**
 * One note, pinned to the verses it comments on. The chapter page lays notes
 * out beside the scripture, so a note's range is what puts it level with its
 * verse.
 */
export const commentaryNoteInput = z
    .object({ ...verseRange, bodyMd: z.string().max(100_000).default("") })
    .refine(rangeIsOrdered, rangeError());
export const commentarySectionInput = z
    .object({
    heading: z.string().trim().max(200).default(""),
    ...verseRange,
    /** Prose introducing the section, above the verse-by-verse notes. */
    bodyMd: z.string().max(100_000).default(""),
    notes: z.array(commentaryNoteInput).max(500).default([]),
    implicationsMd: z.string().max(100_000).default(""),
})
    .refine(rangeIsOrdered, rangeError());
/** One numbered reference. The marker in the text is its index + 1. */
export const commentaryCitationInput = z.object({
    textMd: z.string().max(10_000).default(""),
    url: z.string().url().max(2_000).nullable().default(null),
    bibliographyId: z.string().max(120).nullable().default(null),
});
/** The image a shared link previews with, as uploaded to /api/images; null means the site's own. */
const previewImage = z
    .string()
    .regex(/^[0-9a-f]{64}$/, "Not an uploaded image")
    .nullable()
    .default(null);
export const commentaryInput = z.object({
    title: z.string().trim().max(200).default(""),
    introMd: z.string().max(100_000).default(""),
    tags: z.array(z.string().trim().min(1).max(60)).max(40).default([]),
    image: previewImage,
    status: z.enum(commentaryStatuses).default("draft"),
    sections: z.array(commentarySectionInput).max(200).default([]),
    citations: z.array(commentaryCitationInput).max(200).default([]),
});
/* ---------------- Bibliography ---------------- */
export const bibliographyEntryInput = z.object({
    id: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9][a-z0-9-]*$/, "Use lowercase letters, numbers and hyphens"),
    author: z.string().trim().max(200).default(""),
    title: z.string().trim().max(400).default(""),
    extra: z.string().trim().max(400).nullable().default(null),
    url: z.string().url().max(2_000).nullable().default(null),
    year: z.number().int().min(-3000).max(3000).nullable().default(null),
});
/** The bibliography is saved whole, the way a commentary chapter is. */
export const bibliographyInput = z.object({ entries: z.array(bibliographyEntryInput).max(2_000).default([]) });
/* ---------------- Glossary ---------------- */
/**
 * A word defined once and marked wherever it occurs. The id is not sent: the
 * server derives it from the term (see `glossaryId`), so two entries that
 * would share an anchor on /glossary cannot both be saved.
 */
export const glossaryEntryInput = z.object({
    term: z.string().trim().min(1, "Every entry needs a word").max(100),
    /** Other forms marked as this entry — "cupbearers" under "cupbearer". */
    forms: z.array(z.string().trim().min(1).max(100)).max(20).default([]),
    definitionMd: z.string().max(20_000).default(""),
});
/** Saved whole, like the bibliography. */
export const glossaryInput = z.object({ entries: z.array(glossaryEntryInput).max(2_000).default([]) });
/* ---------------- The site's own pages ---------------- */
/** A page's words — see `SITE_PAGES` in pages.ts for which pages there are. */
export const sitePageInput = z.object({
    title: z.string().trim().min(1, "Every page needs a title").max(200),
    bodyMd: z.string().max(50_000).default(""),
});
/* ---------------- Site statistics ---------------- */
/**
 * What the app reports as a reader moves between pages — the path and, on
 * arrival only, where they came from. Never a query string or a hash: a
 * verse anchor is not a page.
 */
export const pageViewInput = z.object({
    path: z.string().max(500).regex(/^\/[^?#\s]*$/, "A path, without a query string or hash"),
    referrer: z.string().max(2_000).optional(),
});
/**
 * What the app reports when a reader uses a tool. `ref` is a chapter or a verse
 * (OSIS); `detail` is the short thing itself — a search term, a Strong's number
 * and word, a glossary entry — trimmed and cut to 100 characters, which is all
 * the page keeps; `results` is a search's result count. Nothing here names the reader.
 */
export const eventTypes = [
    "search",
    "study_crossrefs",
    "study_translations",
    "study_word",
    "audio_play",
    "highlight",
    "note",
    "bookmark",
    "sign_in",
    "glossary_term",
];
export const EVENT_DETAIL_MAX = 100;
export const eventInput = z
    .object({
    type: z.enum(eventTypes),
    path: pageViewInput.shape.path,
    ref: z
        .string()
        .regex(/^[1-3A-Za-z]{2,8}\.\d{1,3}(?:\.\d{1,3})?$/, "A chapter or verse, like Neh.1 or Neh.1.3")
        .optional(),
    detail: z
        .string()
        .max(2_000)
        .transform((s) => s.trim().slice(0, EVENT_DETAIL_MAX))
        .optional(),
    results: z.number().int().min(0).max(1_000_000).optional(),
})
    .refine((e) => e.type !== "search" || Boolean(e.detail), { message: "A search needs its term", path: ["detail"] });
/** The spans the admin's page offers: a week, a month, a quarter, a year. */
export const statsRanges = [7, 30, 90, 365];
/** Direct (or unnamed), Facebook, a search engine, anywhere else. */
export const statsSources = ["direct", "facebook", "search", "other"];
/* ---------------- The counseling index ---------------- */
/**
 * A need a counselor searches by — anxiety, grief, marriage. The list is
 * curated so one need has one name; `aliases` are the other words a counselor
 * might type for it.
 */
export const counselingTopicInput = z.object({
    id: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9][a-z0-9-]*$/, "Use lowercase letters, numbers and hyphens"),
    name: z.string().trim().min(1, "Every topic needs a name").max(120),
    description: z.string().trim().max(2_000).default(""),
    aliases: z.array(z.string().trim().min(1).max(100)).max(40).default([]),
});
/** Saved whole, like the bibliography. */
export const counselingTopicsInput = z.object({ topics: z.array(counselingTopicInput).max(500).default([]) });
/**
 * A tag: a verse or a run of verses, as `parseRef` reads it. The server checks
 * it against the chapter's commentary, so the shape is all that is checked here.
 */
export const counselingTagInput = z.object({
    topicId: z.string().trim().min(1).max(120),
    ref: z.string().trim().min(1).max(60),
});
/** One chapter's tags, saved whole. */
export const counselingTagsInput = z.object({ tags: z.array(counselingTagInput).max(500).default([]) });
/* ---------------- The blog and the resources ---------------- */
/** The licence's ceiling on ESV verses shown on any one page. */
export const MAX_QUOTED_VERSES = 500;
/**
 * Where a post is published, which is also its address: `/blog/<slug>` is
 * dated and read newest first; `/resources/<slug>` is kept for reference —
 * the charts and notes that stay useful — and listed by title. One table and
 * one editor serve both, since they are written the same way.
 */
export const postSections = ["blog", "resources"];
export const sectionPath = (section) => `/${section}`;
/**
 * The blog is built but not yet open. Until it is, its posts are the admin's
 * alone — out of the header, the lists, the sitemap and the feed — exactly
 * as a draft is.
 */
export const BLOG_OPEN = false;
/** The sections a reader may see. */
export const openSections = BLOG_OPEN ? postSections : ["resources"];
/** /blog/new and /resources/new are where the admin starts one, so nothing may live there. */
export const RESERVED_POST_SLUGS = ["new"];
/**
 * A post, saved whole. It quotes the ESV only by reference — a `{{Neh 1:5-11}}`
 * line in the body — so, like a chapter, it never holds a word of scripture.
 */
export const postInput = z.object({
    section: z.enum(postSections).default("blog"),
    /** The address, /blog/<slug> or /resources/<slug>. Blank means "make one from the title". */
    slug: z
        .string()
        .trim()
        .max(120)
        .regex(/^(?:[a-z0-9][a-z0-9-]*)?$/, "Use lowercase letters, numbers and hyphens in the address")
        .default(""),
    title: z.string().trim().min(1, "A post needs a title").max(200),
    /** A sentence or two for the list and the feed. Blank means the body's opening lines stand in. */
    excerpt: z.string().trim().max(1_000).default(""),
    bodyMd: z.string().max(200_000).default(""),
    tags: z.array(z.string().trim().min(1).max(60)).max(40).default([]),
    image: previewImage,
    status: z.enum(commentaryStatuses).default("draft"),
});
/* ---------------- Images ---------------- */
/** The largest upload the server takes. The editor shrinks a photo well under it first. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
/**
 * The widest an image is kept at. A link preview is drawn around 1200 x 630 and
 * never larger, so anything wider is detail no reader ever sees. The editor
 * redraws to this before uploading, and the server redraws anything that still
 * arrives wider — the picker is not the only way in (`upload_image` over MCP is
 * the other), so the rule has to hold on the server as well.
 */
export const IMAGE_MAX_WIDTH = 1200;
export const imageUrl = (id) => `/api/images/${id}`;
export const changePasswordBody = z.object({
    /** Omitted when the account has no password yet — the session is the proof. */
    currentPassword: z.string().min(1).max(200).optional(),
    newPassword: passwordSchema,
});
/* ---------------- The member's library ---------------- */
/** The four highlighter colours, matching the --hl-* tokens in index.css. */
export const highlightColors = ["yellow", "green", "blue", "pink"];
/**
 * A verse reference, e.g. "Neh.1.3". Shape only — the server runs it through
 * `parseRef` and stores the canonical OSIS, so a member's library can never
 * hold a reference the reader cannot resolve.
 */
const verseRefSchema = z
    .string()
    .trim()
    .max(40)
    .regex(/^[1-9A-Za-z]+\.\d{1,3}\.\d{1,3}$/, "Not a verse reference");
/**
 * Offsets are character positions into the *plain text of one verse*, which is
 * stable in a way positions in the ESV's HTML are not. A selection crossing a
 * verse boundary is saved as one highlight per verse.
 */
export const highlightInput = z
    .object({
    ref: verseRefSchema,
    startOffset: z.number().int().min(0).max(10_000).default(0),
    endOffset: z.number().int().min(0).max(10_000),
    color: z.enum(highlightColors).default("yellow"),
})
    .refine((v) => v.endOffset > v.startOffset, { message: "A highlight ends before it starts", path: ["endOffset"] });
export const memberNoteInput = z.object({
    ref: verseRefSchema,
    bodyMd: z.string().max(20_000).default(""),
});
export const bookmarkInput = z.object({
    ref: verseRefSchema,
    label: z.string().trim().max(200).default(""),
    color: z.enum(highlightColors).default("yellow"),
});
/* ---------------- Signing in by email ---------------- */
/**
 * Where to go once signed in: a path on this site and nothing else, so a link
 * to /login cannot be made to send someone off it. Undefined for anything that
 * is not one.
 */
export function safeNext(next) {
    if (!next || next.length > 2000 || !next.startsWith("/") || next.startsWith("//") || next.includes("\\"))
        return undefined;
    return next;
}
export const magicLinkBody = z.object({
    email: emailSchema,
    /** Only used when the address is new; an existing account keeps its name. */
    name: z.string().trim().max(80).optional(),
    /** Where the link lands once followed, if not /account — the Claude consent page, say. */
    next: z
        .string()
        .optional()
        .transform((n) => safeNext(n)),
});
export const oauthDecisionBody = z.object({ approve: z.boolean() });
/* ---------------- Study tools on public-domain text ---------------- */
/**
 * The translations we hold in full. None of these is the ESV: the licence caps
 * us at 500 stored verses of that, so everything that needs the whole Bible
 * locally — cross-reference previews, a parallel column, the word behind a
 * word — is built on text that is free to keep.
 */
export const translations = ["WEB", "BSB", "KJV"];
export const TRANSLATIONS = [
    {
        id: "WEB",
        name: "World English Bible",
        notice: "The World English Bible is in the public domain.",
        url: "https://ebible.org/web/",
    },
    {
        id: "BSB",
        name: "Berean Standard Bible",
        notice: "The Berean Standard Bible was placed in the public domain in 2023.",
        url: "https://berean.bible",
    },
    {
        id: "KJV",
        name: "King James Version",
        notice: "The King James Version (1769) is in the public domain.",
        url: "https://ebible.org/eng-kjv/",
    },
];
export const translationName = (id) => TRANSLATIONS.find((t) => t.id === id)?.name ?? id;
/** Everything on a page that is not the ESV, credited in one line. */
export const OPEN_TEXT_CREDITS = "Cross-references from OpenBible.info (CC BY). Parallel texts, the interlinear and Strong’s lexicons are public domain: the World English Bible (ebible.org), the Berean Standard Bible (berean.bible) and Strong’s Exhaustive Concordance (1890) via openscriptures.org.";
export const strongsLanguages = ["hebrew", "aramaic", "greek"];
//# sourceMappingURL=api.js.map